// src/redux/notifications/notificationsSlice.ts

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  NotificationDTO,
  UnreadCountDTO,
  NotificationPreferenceDTO,
  NotificationPreferencePatchDTO,
} from "../../types/notifications/notificationsTypes";
import { notificationsApi } from "../../services/notifications/notificationsApi";
import { AppDispatch, RootState } from "../storeTypes";
import {
  ensureNotificationsSocketConnected,
  onNotification,
} from "../../realtime/notificationsSocket";

const MAX_ITEMS = 200;
const DEFAULT_PAGE_SIZE = 20;

type State = {
  items: NotificationDTO[];
  unread: number;
  loading: boolean;
  error?: string | null;
  wsBound: boolean;

  page: number;
  hasMore: boolean;
  total: number;

  preferences: NotificationPreferenceDTO[];
  prefLoading: boolean;
  prefError?: string | null;
};

const initialState: State = {
  items: [],
  unread: 0,
  loading: false,
  error: null,
  wsBound: false,
  page: 0,
  hasMore: true,
  total: 0,

  preferences: [],
  prefLoading: false,
  prefError: null,
};

function mergeNotifications(
  existing: NotificationDTO[],
  incoming: NotificationDTO[],
  append: boolean
): NotificationDTO[] {
  const base = append ? [...existing] : [];
  const seen = new Set(base.map((n) => n.id));

  for (const n of incoming) {
    if (!seen.has(n.id)) {
      base.push(n);
      seen.add(n.id);
    }
  }

  return base.slice(0, MAX_ITEMS);
}

function normalizeError(error: any, fallback: string): string {
  if (!error) return fallback;

  if (typeof error === "string") return error;

  if (typeof error?.detail === "string") return error.detail;
  if (typeof error?.message === "string") return error.message;

  if (typeof error?.response?.data?.detail === "string") {
    return error.response.data.detail;
  }

  if (typeof error?.response?.data?.message === "string") {
    return error.response.data.message;
  }

  return fallback;
}

// --- Thunks ---------------------------------------------------------------

export const fetchNotifications = createAsyncThunk<
  {
    page: number;
    results: NotificationDTO[];
    count: number;
    next: string | null;
    previous: string | null;
    append: boolean;
  },
  { page?: number; pageSize?: number; append?: boolean } | undefined,
  { rejectValue: string }
>("notifications/list", async (params, { rejectWithValue }) => {
  try {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? DEFAULT_PAGE_SIZE;
    const append = params?.append ?? page > 1;

    const data = await notificationsApi.list(page, pageSize);

    return {
      page,
      results: data.results ?? [],
      count: data.count ?? 0,
      next: data.next ?? null,
      previous: data.previous ?? null,
      append,
    };
  } catch (e: any) {
    return rejectWithValue(
      normalizeError(e, "Failed to fetch notifications")
    );
  }
});

export const fetchUnreadCount = createAsyncThunk<
  UnreadCountDTO,
  void,
  { rejectValue: string }
>("notifications/unreadCount", async (_, { rejectWithValue }) => {
  try {
    return await notificationsApi.unreadCount();
  } catch (e: any) {
    return rejectWithValue(
      normalizeError(e, "Failed to fetch unread count")
    );
  }
});

export const markRead = createAsyncThunk<
  NotificationDTO,
  number,
  { rejectValue: string }
>("notifications/markRead", async (id, { rejectWithValue }) => {
  try {
    return await notificationsApi.markRead(id);
  } catch (e: any) {
    return rejectWithValue(
      normalizeError(e, "Failed to mark as read")
    );
  }
});

export const markAllRead = createAsyncThunk<
  { updated: number },
  void,
  { rejectValue: string; state: RootState }
>("notifications/markAllRead", async (_, { rejectWithValue }) => {
  try {
    return await notificationsApi.markAllRead();
  } catch (e: any) {
    return rejectWithValue(
      normalizeError(e, "Failed to mark all as read")
    );
  }
});

export const fetchNotificationPreferences = createAsyncThunk<
  NotificationPreferenceDTO[],
  void,
  { rejectValue: string }
>("notifications/fetchPreferences", async (_, { rejectWithValue }) => {
  try {
    return await notificationsApi.listPreferences();
  } catch (e: any) {
    return rejectWithValue(
      normalizeError(e, "Failed to load notification preferences")
    );
  }
});

export const updateNotificationPreference = createAsyncThunk<
  NotificationPreferenceDTO,
  { id: number; patch: NotificationPreferencePatchDTO },
  { rejectValue: string }
>("notifications/updatePreference", async ({ id, patch }, { rejectWithValue }) => {
  try {
    return await notificationsApi.updatePreference(id, patch);
  } catch (e: any) {
    return rejectWithValue(
      normalizeError(e, "Failed to update notification preference")
    );
  }
});

export const resetNotificationPreferences = createAsyncThunk<
  NotificationPreferenceDTO[],
  void,
  { rejectValue: string }
>("notifications/resetPreferences", async (_, { rejectWithValue }) => {
  try {
    return await notificationsApi.resetPreferences();
  } catch (e: any) {
    return rejectWithValue(
      normalizeError(e, "Failed to reset preferences")
    );
  }
});

// --- Slice ---------------------------------------------------------------

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    pushRealtime(state, action: PayloadAction<NotificationDTO>) {
      const n = action.payload;
      const idx = state.items.findIndex((x) => x.id === n.id);

      if (idx >= 0) {
        state.items[idx] = n;
      } else {
        state.items.unshift(n);

        if (state.items.length > MAX_ITEMS) {
          state.items.pop();
        }

        if (n.is_read === false) {
          state.unread += 1;
        }
      }
    },

    setWsBound(state, action: PayloadAction<boolean>) {
      state.wsBound = action.payload;
    },

    resetNotificationsState(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      // --- fetchNotifications ---
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, { payload }) => {
        state.loading = false;

        const { results, append, page, count, next } = payload;
        const incoming = results || [];

        state.items = mergeNotifications(state.items, incoming, append);
        state.page = page;
        state.total = count;
        state.hasMore = !!next && state.items.length < MAX_ITEMS;
      })
      .addCase(fetchNotifications.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload || "Error";
      })

      // --- unread count ---
      .addCase(fetchUnreadCount.fulfilled, (state, { payload }) => {
        state.unread = payload.unread;
      })

      // --- mark read ---
      .addCase(markRead.fulfilled, (state, { payload }) => {
        const idx = state.items.findIndex((x) => x.id === payload.id);

        if (idx >= 0) {
          const wasUnread = state.items[idx].is_read === false;
          state.items[idx] = payload;

          if (wasUnread && payload.is_read) {
            state.unread = Math.max(0, state.unread - 1);
          }
        } else if (payload.is_read) {
          state.unread = Math.max(0, state.unread - 1);
        }
      })

      // --- mark all read ---
      .addCase(markAllRead.fulfilled, (state) => {
        state.items = state.items.map((n) => ({
          ...n,
          is_read: true,
        }));
        state.unread = 0;
      })

      // --- preferences load ---
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.prefLoading = true;
        state.prefError = null;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, { payload }) => {
        state.prefLoading = false;
        state.preferences = payload;
      })
      .addCase(fetchNotificationPreferences.rejected, (state, { payload }) => {
        state.prefLoading = false;
        state.prefError = payload || "Error";
      })

      // --- preferences update ---
      .addCase(updateNotificationPreference.fulfilled, (state, { payload }) => {
        const idx = state.preferences.findIndex((p) => p.id === payload.id);

        if (idx >= 0) {
          state.preferences[idx] = payload;
        }
      })
      .addCase(updateNotificationPreference.rejected, (state, { payload }) => {
        state.prefError = payload || "Error";
      })

      // --- preferences reset ---
      .addCase(resetNotificationPreferences.pending, (state) => {
        state.prefLoading = true;
        state.prefError = null;
      })
      .addCase(resetNotificationPreferences.fulfilled, (state, { payload }) => {
        state.prefLoading = false;
        state.preferences = payload;
      })
      .addCase(resetNotificationPreferences.rejected, (state, { payload }) => {
        state.prefLoading = false;
        state.prefError = payload || "Error resetting preferences";
      });
  },
});

export const {
  pushRealtime,
  setWsBound,
  resetNotificationsState,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;

// --- Helpers to init WS and bind once -----------------------------------

export const initNotificationsRealtime =
  (token: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { wsBound } = getState().notifications;

    if (wsBound) return;

    ensureNotificationsSocketConnected(token);

    onNotification((payload) => {
      dispatch(pushRealtime(payload));
    });

    dispatch(setWsBound(true));
  };